import i18next from 'i18next';
import { EXECUTE_ARBITRARY_SQL_OPERATION } from 'app/store/__generated__/operations';
import { toStringFqn } from 'app/lib/utils';
import { QUERY_HISTORY_FQN, QUERY_HISTORY_FQN_STRING } from 'app/lib/constants';
import { doExecuteSqlQuery } from 'app/store/snowflakeQueries/actions.js';
import { QueryKeys } from 'app/store/snowflakeQueries/reducer.js';
import { selectSnowflakeConnection } from 'app/store/healthcheck/selectors';
import { doCreateExploration } from 'app/store/exploreData/actions.js';
import { selectNextAvailableIndexForEntityId } from 'app/store/exploreData/selectors.js';
import {
    LOAD_WAREHOUSES,
    LOAD_WAREHOUSE_USAGE,
    SELECT_WAREHOUSES,
    SET_WAREHOUSE_FILTER,
    DO_LOAD_WAREHOUSE_CUBE,
    CHECK_WAREHOUSE_USAGE_TABLE_ACCESS,
    SET_WAREHOUSE_CUBE_DATE_FILTER,
    SET_WAREHOUSE_CUBE_WAREHOUSE_FILTER,
    SET_WAREHOUSE_CUBE_USER_FILTER,
    SET_WAREHOUSE_CUBE_SECONDARY_DIMENSION,
    SET_WAREHOUSE_CUBE_PRIMARY_DIMENSION,
    SET_WAREHOUSE_CUBE_MEASURE,
    SET_WAREHOUSE_CUBE_DATE_FILTER_AND_BIN,
    RESET_WAREHOUSE_CUBE_FILTERS,
    CREATE_EXPLORE,
    GET_QUERY_HISTORY_COLUMNS,
    SELECT_TAB,
    SELECT_CHART_AREA,
    CHANGE_CHART_TYPE,
    GET_USAGE_JOB_DEFINITION,
    GET_USAGE_JOB_DEFINITIONS
} from './types.js';
import { warehouseUsageSql, warehouseCubeSql } from './sql.js';
import {
    selectWarehouseCubeUserFilter,
    selectWarehouseCubeDateFilter,
    selectWarehouseCubeWarehouseFilter,
    selectWarehouseCubeSecondaryDimension,
    selectWarehouseCubePrimaryDimension,
    selectWarehouseCubeSelectedMeasure,
    selectWarehouseCubeCaluclatedDateBin,
    selectQueryDetailsInitialModel,
    selectUsageWarehouseJob
} from './selectors.js';
import { UsageJobOperationType, UsageJobKey } from './utils.js';

export const doLoadWarehouseUsage = () => ({
    type: LOAD_WAREHOUSE_USAGE,
    effect: ({ store }) => {
        const connection = selectSnowflakeConnection(store.getState());
        const accountName = connection.account;

        store.dispatch(
            doExecuteSqlQuery({
                queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_USAGE,
                sql: warehouseUsageSql(accountName),
                fullData: true,
                description: i18next.t('queries:usage.warehouseUsage')
            })
        );
    }
});

export const doSelectWarehouses = (warehouses) => ({
    type: SELECT_WAREHOUSES,
    payload: {
        warehouses
    }
});

export const doSetWarehouseFilter = (warehouses) => ({
    type: SET_WAREHOUSE_FILTER,
    payload: { warehouses }
});

export const doLoadWarehouses = () => ({
    type: LOAD_WAREHOUSES,
    effect: ({ store }) => {
        store.dispatch(
            doExecuteSqlQuery({
                queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSES,
                sql: 'SHOW WAREHOUSES',
                skipSqlTranslate: true,
                description: i18next.t('queries:usage.warehouses')
            })
        );
    }
});

export const doVerifyWarehouseUsageTableAccess = (warehouseJob) => ({
    type: CHECK_WAREHOUSE_USAGE_TABLE_ACCESS,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: () => {
        const fqn = toStringFqn({
            ...warehouseJob.operation.targetSchema,
            table: 'DATAMEER_WAREHOUSE_USAGE'
        });

        return { sql: `DESC TABLE ${fqn}` };
    }
});

export const doLoadWarehouseCube = (fetchWarehousesAndUsers) => ({
    type: DO_LOAD_WAREHOUSE_CUBE,
    effect: ({ store }) => {
        const state = store.getState();
        const job = selectUsageWarehouseJob(state);

        if (!job) {
            return;
        }

        const fqn = toStringFqn({
            ...job.operation.targetSchema,
            table: 'DATAMEER_WAREHOUSE_USAGE'
        });
        const dateFilter = selectWarehouseCubeDateFilter(state);
        const warehouseFilter = selectWarehouseCubeWarehouseFilter(state);
        const userFilter = selectWarehouseCubeUserFilter(state);
        const secondaryDimension = selectWarehouseCubeSecondaryDimension(state);
        const primaryDimension = selectWarehouseCubePrimaryDimension(state);
        const selectedMeasure = selectWarehouseCubeSelectedMeasure(state);
        const dateBin = selectWarehouseCubeCaluclatedDateBin(state);

        store.dispatch(
            doExecuteSqlQuery({
                queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE,
                sql: warehouseCubeSql({
                    fqn,
                    dateFilter,
                    warehouseFilter,
                    userFilter,
                    primaryDimension,
                    secondaryDimension,
                    selectedMeasure,
                    dateBin
                }),
                fullData: true,
                description: i18next.t('queries:usage.warehouseCube')
            })
        );
        if (fetchWarehousesAndUsers) {
            store.dispatch(
                doExecuteSqlQuery({
                    queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE_WAREHOUSE_VALUES,
                    sql: `SELECT DISTINCT WAREHOUSE_NAME FROM ${fqn} WHERE WAREHOUSE_NAME NOT LIKE '%TEST%' ORDER BY 1`,
                    fullData: true,
                    description: i18next.t('queries:usage.cubeWarehouses')
                })
            );

            store.dispatch(
                doExecuteSqlQuery({
                    queryKey: QueryKeys.USAGE_REPORTING_WAREHOUSE_CUBE_USER_VALUES,
                    sql: `SELECT DISTINCT QUERY_USER_NAME FROM ${fqn} WHERE QUERY_USER_NAME IS NOT NULL ORDER BY 1`,
                    fullData: true,
                    description: i18next.t('queries:usage.cubeUsers')
                })
            );
        }
    }
});

export const doSetWarehouseCubeDateFilter = (range) => ({
    type: SET_WAREHOUSE_CUBE_DATE_FILTER,
    payload: range,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSetWarehouseCubeWarehouseFilter = (values) => ({
    type: SET_WAREHOUSE_CUBE_WAREHOUSE_FILTER,
    payload: values,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSetWarehouseCubeUserFilter = (values) => ({
    type: SET_WAREHOUSE_CUBE_USER_FILTER,
    payload: values,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doResetWarehouseCubeFilters = () => ({
    type: RESET_WAREHOUSE_CUBE_FILTERS,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSetWarehouseCubeSecondaryDimension = (value) => ({
    type: SET_WAREHOUSE_CUBE_SECONDARY_DIMENSION,
    payload: value,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSetWarehouseCubePrimaryDimension = (value) => ({
    type: SET_WAREHOUSE_CUBE_PRIMARY_DIMENSION,
    payload: value,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSetWarehouseCubeMeasure = (value) => ({
    type: SET_WAREHOUSE_CUBE_MEASURE,
    payload: value,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSetWarehouseCubeDateFilerAndBin = (value) => ({
    type: SET_WAREHOUSE_CUBE_DATE_FILTER_AND_BIN,
    payload: value,
    effect: ({ store }) => store.dispatch(doLoadWarehouseCube())
});

export const doSelectTab = (id) => ({
    type: SELECT_TAB,
    payload: { id }
});

export const doCreateExplore = () => ({
    type: CREATE_EXPLORE,
    effect: ({ store }) => {
        const state = store.getState();
        const model = selectQueryDetailsInitialModel(state);
        const index = selectNextAvailableIndexForEntityId(state, {
            entityId: QUERY_HISTORY_FQN_STRING
        });
        const onSucceeded = (id) => {
            store.dispatch(doSelectTab(id));
        };
        store.dispatch(
            doCreateExploration({
                tableFqn: QUERY_HISTORY_FQN,
                model: {
                    ...model,
                    name: i18next.t('usage:warehouseSpend.tabs.queryDetails_other', {
                        count: index
                    })
                },
                onSucceeded,
                skipRowCount: true
            })
        );
    }
});

export const doGetQueryHistoryColumns = () => ({
    type: GET_QUERY_HISTORY_COLUMNS,
    api: { version: 2, name: 'FetchSnowflakeTableColumns' },
    params: QUERY_HISTORY_FQN
});

export const doSelectChartArea = (selection) => ({
    type: SELECT_CHART_AREA,
    payload: selection
});

export const doChangeChartType = (type) => ({
    type: CHANGE_CHART_TYPE,
    payload: type
});

const doGetUsageJobDefinition = (key) => ({
    type: GET_USAGE_JOB_DEFINITION,
    api: { version: 2, name: 'GetUsageJobDefinition' },
    meta: { key },
    params: { operationType: UsageJobOperationType[key] }
});

export const doGetUsageJobDefinitions = () => ({
    type: GET_USAGE_JOB_DEFINITIONS,
    effect: ({ store }) => {
        store.dispatch(doGetUsageJobDefinition(UsageJobKey.SNOWFLAKE_HISTORY_INDEXING_JOB));
        store.dispatch(doGetUsageJobDefinition(UsageJobKey.WAAREHOUSE_JOB));
        store.dispatch(doGetUsageJobDefinition(UsageJobKey.TCO_JOB));
    }
});
